import React, { useEffect, useState } from 'react';
import { useGetAllTransactions, useGetContractTransactionsExport } from '../hooks/api/transactionsApi';
import { rowsPerPageOptions } from '../../../constants/pagination';
import { transactionsColumns } from '../enums/columns';
import { useParams } from 'react-router';
import DataTable from '../../../components/DataTable/DataTable';
import useListHook from '../../../hooks/lists/useListHook';
import CardRowCard from '../../../components/CardRow/CardRowCard';
import CardRow from '../../../components/CardRow/CardRow';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import FileExport from '../../../components/FileExport/FileExport';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';
import useAppContext from '../../../hooks/context/useAppContext';
import DataTableBodyCellDecimal from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellDecimal';

const columns = [
  transactionsColumns.createdAt,
  transactionsColumns.exchangeProductName,
  transactionsColumns.purchaseType,
  transactionsColumns.priceSource,
  transactionsColumns.rate,
  transactionsColumns.price,
  transactionsColumns.quantity,
  transactionsColumns.tariff1,
  transactionsColumns.tariff2,
  transactionsColumns.tariff3,
  transactionsColumns.tariff4,
  transactionsColumns.tariff5,
  transactionsColumns.tariff6,
];

const initialValues = {
  page: 0,
  rowsPerPage: 10,
  order: 'desc',
  orderBy: 'createdAt',
};

const TransactionsList = () => {
  const intl = useIntl();
  const { contractId } = useParams();
  const { user } = useAppContext();
  const { getAllTransactions, data, loading, error } = useGetAllTransactions();
  const { getContractTransactionsExport, data: exportData, error: exportError, loading: exportLoading } = useGetContractTransactionsExport();
  const {
    totalCount,
    orderByColumn,
    orderDirection,
    page,
    rows,
    rowsPerPage,
    setOrderByColumn,
    setOrderDirection,
    setPage,
    setRows,
    setRowsPerPage,
    setTotalCount,
  } = useListHook(initialValues);

  const [searchQuery, setSearchQuery] = useState({});

  useEffect(() => {
    const query = {
      page: page,
      size: rowsPerPage,
      sort: {
        type: orderDirection,
        column: orderByColumn,
      },
      filters: [
        { column: 'contractId', values: [parseInt(contractId)] },
        { column: 'organizationId', values: user?.customerIds },
      ],
    };
    (async () => {
      setSearchQuery(query);
      await getAllTransactions(query);
    })();
  }, [page, rowsPerPage, orderDirection, orderByColumn, contractId, getAllTransactions, user, setSearchQuery]);

  useEffect(() => {
    if (data) {
      setRows(
        data.data.map((row) => {
          const output = {
            id: row?.id,
            createdAt: row?.createdAt && <FormattedDate value={row.createdAt} />,
            contractId: row?.contract?.id,
            purchaseType: row?.purchaseType?.name,
            requestPeriod: row?.contract?.contractPeriod,
            exchangeProductName: row?.exchangeProduct?.name,
            priceSource: row?.priceSource?.name,
            currency: row?.currency?.id,
            quantity: <DataTableBodyCellDecimal value={row?.quantity} />,
            rate: <DataTableBodyCellDecimal value={row?.rate} />,
            price: <DataTableBodyCellDecimal value={row?.price} />,
            tariff1: null,
            tariff2: null,
            tariff3: null,
            tariff4: null,
            tariff5: null,
            tariff6: null,
          };

          row?.tariffPrice &&
            row.tariffPrice.forEach((tariff) => {
              output[`tariff${tariff.idTariff}`] = <DataTableBodyCellDecimal value={tariff?.value} />;
            });

          return output;
        }),
      );
      setTotalCount(data.totalCount);
    }
  }, [data, setRows, setTotalCount]);

  return (
    <>
      <SectionHeader>
        <FormattedMessage id={'app.contract.realizedTransactions.label'} />
        <FileExport
          getAllEntityExport={getContractTransactionsExport}
          tooltip={<FormattedMessage id={'app.contract.realizedTransactions.label.export'} />}
          fileName={intl.formatMessage({ id: 'app.contract.realizedTransactions.label.export.filename' })}
          data={exportData}
          searchQuery={searchQuery}
          exportError={exportError}
          key={1}
        />
      </SectionHeader>
      <CardRow>
        <CardRowCard>
          <DataTable
            lineHover
            displayToolbar={false}
            columns={columns}
            totalCount={totalCount}
            rowsPerPageOptions={rowsPerPageOptions}
            data={rows || []}
            loading={loading || exportLoading}
            error={error}
            orderDirection={orderDirection}
            setOrderDirection={setOrderDirection}
            orderByColumn={orderByColumn}
            setOrderByColumn={setOrderByColumn}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        </CardRowCard>
      </CardRow>
    </>
  );
};

TransactionsList.propTypes = {};

export default TransactionsList;
